/* AO - send a stated vehicle to the customer's account.
 *
 * The local half of the garage (ao-garage.js) remembers what someone drives in
 * localStorage. That is device-local and anonymous: it does not survive a new
 * phone, and it cannot be used to build an email segment. This is the other
 * half. When the person clicking happens to be signed in, their choice is
 * written to their Shopify customer record as `stated:*` tags.
 *
 * WHY THE MENU CLICK IS WORTH SENDING AT ALL. Purchase history already resolves
 * MODEL for ~18,400 customers. What it cannot resolve is GENERATION for the
 * newest cars: Model Y 2026 "Juniper" and Model 3 2024 "Highland" have zero
 * purchase-proven owners, because the year tags are deliberately
 * over-inclusive, so no part anyone has ever bought isolates either. A
 * generation chip is the only source for that, and it is what the round-eight
 * removal of the YMM finder took away.
 *
 * WHY IT GOES THROUGH AN APP PROXY. `/apps/garage/set` is a Shopify app proxy
 * in front of a Cloudflare Worker. Shopify signs the request and attaches
 * `logged_in_customer_id`, which is the only way the Worker can know that a
 * request claiming to be customer 12345 really is. Nothing here sends an id -
 * it cannot, and that is the point. See garage-endpoint/README.md.
 *
 * 🔴 FIRE AND FORGET, ON PURPOSE. The click is a navigation: the browser is
 * already leaving for the collection page. This must never delay it, never
 * block it, and never surface an error - a customer clicking "New Model Y"
 * wants Model Y parts, not a message about a tagging service. Every failure
 * path below is silent, and the local garage has already recorded the choice
 * either way, so nothing is lost when the POST does not land.
 */
(function () {
  // Loaded from both the header and the model-menu section, like ao-garage.js.
  // Without this guard the delegated listener binds twice and every statement
  // is posted twice.
  if (window.AOGarageEndpoint) return;

  var ENDPOINT = '/apps/garage/set';

  // Logged-out is the NORMAL case, not an error: the endpoint answers
  // `{ ok: true, stored: false }` and the choice stays in localStorage. This
  // remembers it so that when the customer signs in later, the vehicle they
  // already picked can be sent without asking them again.
  var PENDING_KEY = 'ao_garage_pending';

  function readPending() {
    try {
      var raw = window.localStorage.getItem(PENDING_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writePending(value) {
    try {
      if (value === null) window.localStorage.removeItem(PENDING_KEY);
      else window.localStorage.setItem(PENDING_KEY, JSON.stringify(value));
    } catch (e) {
      /* private mode or quota - the statement is simply not retried */
    }
  }

  /**
   * @param {string} generation  a generations.json id, e.g. 'model-y-refresh'
   * @param {string} [trim]      a trim id, when the surface knows one
   */
  function send(generation, trim) {
    if (!generation) return;

    var payload = { generation: generation };
    if (trim) payload.trim = trim;

    // `keepalive` is what makes this work at all. The click navigates away
    // immediately, and without it the browser cancels the in-flight request on
    // unload - which would have failed only for real customers, and only
    // sometimes, while looking fine in any test that did not navigate.
    var request;
    try {
      request = window.fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      });
    } catch (e) {
      writePending(payload);
      return;
    }

    request
      .then(function (response) {
        if (!response.ok) {
          // 4xx means the endpoint refused this generation - an id the
          // allowlist does not carry. Retrying cannot help, and the generator
          // asserts against generations.json precisely so this cannot ship.
          writePending(null);
          return null;
        }
        return response.json();
      })
      .then(function (body) {
        if (!body) return;
        // stored:false means nobody was signed in. Hold it and try again after
        // they are - the endpoint sees no cookies (app proxies strip them both
        // ways), so the theme is the only place this can be remembered.
        writePending(body.stored ? null : payload);
      })
      .catch(function () {
        // Offline, blocked, or the page went away mid-flight. The local garage
        // still holds the vehicle; keep the statement for the next page.
        writePending(payload);
      });
  }

  /* One delegated listener for both surfaces, matching ao-garage.js. The
     desktop panel and the mobile drawer are different markup in different
     sections and the drawer is moved to the end of <body> by Focal, so nothing
     can hold a reference to both. Keying off the attribute means a third
     surface needs no JS at all. */
  document.addEventListener('click', function (event) {
    var link = event.target.closest ? event.target.closest('a[href]') : null;
    if (!link) return;

    // A generation chip states a generation outright.
    var generation = link.getAttribute('data-generation-id');

    /* Failing that, a hub link states one ONLY where the vehicle has a single
       generation - Cybertruck. Clicking "Model Y" says which model but not
       which generation, and recording a generation there would be inventing
       precision the click does not contain. The attribute is only rendered for
       vehicles that qualify, so its presence is the whole test. */
    if (!generation) {
      var scope = link.closest('[data-hub-generation-id]');
      if (scope) generation = scope.getAttribute('data-hub-generation-id');
    }

    if (generation) send(generation, link.getAttribute('data-trim-id') || null);
  });

  /* Retry a held statement once the customer is signed in.
     `window.customer` is set in layout/theme.liquid from Liquid, so it is the
     server's view of the session rather than a guess. */
  function flush() {
    var pending = readPending();
    if (!pending || !window.customer) return;
    send(pending.generation, pending.trim);
  }

  window.AOGarageEndpoint = { send: send, flush: flush };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', flush);
  } else {
    flush();
  }
})();
